import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiFetch } from "./api";
import { useAuth } from "./auth";
import { enableWebPush } from "./webPush";

const NotificationsContext = createContext(null);

// Provider unico per: badge non letti (polling + focus + push), e abilitazione Web Push.
// Riusa l'endpoint esistente /api/notifications/unread-count e il flusso notify() del backend.
export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushTick, setPushTick] = useState(0);

  const refreshUnread = useCallback(async () => {
    try {
      const res = await apiFetch("/notifications/unread-count");
      setUnreadCount(res.count || 0);
    } catch {
      /* ignore */
    }
  }, []);

  // Ri-sottoscrizione silenziosa al login SOLO se il permesso è già concesso (nessun prompt automatico).
  // La richiesta del permesso avviene esclusivamente dal banner "Attiva notifiche".
  useEffect(() => {
    if (user && !user.is_impersonated) enableWebPush(false).catch(() => {});
  }, [user?.id, user?.is_impersonated]);

  // Badge/lista aggiornati automaticamente: polling, ritorno in primo piano, messaggi dal SW.
  useEffect(() => {
    if (!user) return undefined;
    refreshUnread();
    const interval = setInterval(refreshUnread, 20000);
    const onVisible = () => { if (document.visibilityState === "visible") refreshUnread(); };
    document.addEventListener("visibilitychange", onVisible);
    const onMessage = (event) => {
      if (event.data && event.data.type === "push") {
        refreshUnread();
        setPushTick((t) => t + 1);
      }
    };
    navigator.serviceWorker?.addEventListener?.("message", onMessage);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      navigator.serviceWorker?.removeEventListener?.("message", onMessage);
    };
  }, [user?.id, refreshUnread]);

  return (
    <NotificationsContext.Provider value={{ unreadCount, refreshUnread, pushTick }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationsContext) || { unreadCount: 0, refreshUnread: () => {}, pushTick: 0 };
}
